// Enhanced event bus with typed game events
import { GlobalBus } from '@core/Bus/EventBus';

// Base game event types - can be extended by specific games
export interface GameEvents {
  'bet:placed'?: { side: 'A' | 'B'; amount: number; betNo: number };
  'bet:cashed': {
    side: 'A' | 'B';
    amount: number;
    multiplier: number;
    winAmount: number;
  };
  'bet:lost': { side: 'A' | 'B'; amount: number };
  'round:started': { roundId: string };
  'round:crashed': { multiplier: number; roundId: string };
  'multiplier:updated': { value: number; phase: string };
  'player:joined': { playerCount: number };
  'animation:trigger': {
    type: 'celebration' | 'explosion' | 'warning';
    data?: any;
  };
  'sound:play': { soundId: string; volume?: number };
  'ui:notification': { message: string; type: 'success' | 'error' | 'info' };
}

// Generic event bus interface that can be extended
export interface IGameEventBus<T extends GameEvents = GameEvents> {
  publish<K extends keyof T>(event: K, data: T[K]): void;
  subscribe<K extends keyof T>(
    event: K,
    handler: (data: T[K]) => void
  ): () => void;
  subscribeMultiple<K extends keyof T>(
    events: K[],
    handler: (event: K, data: T[K]) => void
  ): () => void;
}

export class GameEventBus implements IGameEventBus<GameEvents> {
  private static instance: GameEventBus;

  static getInstance(): GameEventBus {
    if (!GameEventBus.instance) {
      GameEventBus.instance = new GameEventBus();
    }
    return GameEventBus.instance;
  }

  // Typed publish method
  publish<K extends keyof GameEvents>(event: K, data: GameEvents[K]): void {
    console.log(`[GameEventBus] Publishing: ${event}`, data);
    GlobalBus.emit(event, data);
  }

  // Typed subscribe method
  subscribe<K extends keyof GameEvents>(
    event: K,
    handler: (data: GameEvents[K]) => void
  ): () => void {
    console.log(`[GameEventBus] Subscribing to: ${event}`);
    return GlobalBus.on(event, handler);
  }

  // Subscribe to multiple events at once
  subscribeMultiple<K extends keyof GameEvents>(
    events: K[],
    handler: (event: K, data: GameEvents[K]) => void
  ): () => void {
    const unsubscribers = events.map(event =>
      this.subscribe(event, data => handler(event, data))
    );

    return () => unsubscribers.forEach(unsub => unsub());
  }

  // Namespace for different systems
  namespace(prefix: string) {
    return {
      publish: (event: string, data: any) =>
        this.publish(`${prefix}:${event}` as any, data),
      subscribe: (event: string, handler: (data: any) => void) =>
        this.subscribe(`${prefix}:${event}` as any, handler),
    };
  }
}

export const gameEvents = GameEventBus.getInstance();
